<script setup>
// Inspector do nó "Negócio no funil": funil, etapas liberadas e o modo de
// cada ação (automático / sugerir / desligado). O servidor lê esta config.
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import FunnelsAPI from 'dashboard/api/funnels';

const props = defineProps({ data: { type: Object, required: true } });
const emit = defineEmits(['update', 'remove', 'close']);
const { t } = useI18n();

const funnels = ref([]);
const MODES = ['auto', 'suggest', 'off'];
const ACTIONS = [
  { key: 'moveMode', fallback: 'auto' },
  { key: 'lostMode', fallback: 'auto' },
  { key: 'wonMode', fallback: 'suggest' },
];
const actionLabel = key =>
  ({
    moveMode: t('AI_AGENTS.FLOW.CRM.MODE_MOVE'),
    lostMode: t('AI_AGENTS.FLOW.CRM.MODE_LOST'),
    wonMode: t('AI_AGENTS.FLOW.CRM.MODE_WON'),
  })[key];
const modeLabel = mode =>
  ({
    auto: t('AI_AGENTS.FLOW.CRM.MODE_AUTO'),
    suggest: t('AI_AGENTS.FLOW.CRM.MODE_SUGGEST'),
    off: t('AI_AGENTS.FLOW.CRM.MODE_OFF'),
  })[mode];

const funnel = computed(() =>
  funnels.value.find(f => f.id === Number(props.data.funnelId))
);
// Ganho/perdido não são destino de "mover": a IA usa marcar_ganho/perdido.
const TERMINAL = /ganh|won|perd|lost/i;
const stages = computed(() =>
  Object.entries(funnel.value?.stages || {})
    .map(([name, cfg]) => ({
      name,
      label: cfg?.label || cfg?.name || name,
      position: Number(cfg?.position ?? 999),
    }))
    .filter(s => !TERMINAL.test(s.label))
    .sort((a, b) => a.position - b.position)
);
const allowed = computed(() => props.data.allowedStages || []);

const toggleStage = name => {
  const next = allowed.value.includes(name)
    ? allowed.value.filter(s => s !== name)
    : [...allowed.value, name];
  emit('update', { allowedStages: next });
};

const onFunnelChange = e =>
  emit('update', {
    funnelId: Number(e.target.value) || null,
    allowedStages: [],
  });

onMounted(async () => {
  try {
    const { data } = await FunnelsAPI.get();
    funnels.value = data || [];
  } catch {
    funnels.value = [];
  }
});
</script>

<template>
  <div class="p-4">
    <div class="mb-3 flex items-center justify-between">
      <div class="flex items-center gap-2.5">
        <span
          class="flex size-8 shrink-0 items-center justify-center rounded-xl bg-n-teal-3 text-n-teal-11"
        >
          <span class="i-lucide-briefcase-business text-base" />
        </span>
        <h3 class="text-sm font-semibold text-n-slate-12">
          {{ data.label || t('AI_AGENTS.FLOW.CRM.TITLE_DEAL') }}
        </h3>
      </div>
      <button class="i-lucide-x text-n-slate-10" @click="emit('close')" />
    </div>

    <div class="mb-3 rounded-lg border border-n-weak bg-n-alpha-1 p-3">
      <p class="text-sm text-n-slate-12">
        {{ t('AI_AGENTS.FLOW.TOOLS.tool_deal.DESC') }}
      </p>
    </div>

    <label
      class="mb-3 flex cursor-pointer items-center justify-between rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2.5"
    >
      <span class="text-sm font-medium text-n-slate-12">
        {{ t('AI_AGENTS.FLOW.TOOLS.ENABLED') }}
      </span>
      <input
        type="checkbox"
        class="size-4 shrink-0 accent-[#00897b]"
        :checked="data.enabled !== false"
        @change="emit('update', { enabled: $event.target.checked })"
      />
    </label>

    <label class="mb-1 block text-xs font-medium text-n-slate-11">
      {{ t('AI_AGENTS.FLOW.CRM.FUNNEL') }}
    </label>
    <select
      :value="data.funnelId || ''"
      class="w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12"
      @change="onFunnelChange"
    >
      <option value="">{{ t('AI_AGENTS.FLOW.CRM.FUNNEL_PICK') }}</option>
      <option v-for="f in funnels" :key="f.id" :value="f.id">
        {{ f.name }}
      </option>
    </select>

    <template v-if="funnel">
      <p class="mb-1 mt-3 text-xs font-medium text-n-slate-11">
        {{ t('AI_AGENTS.FLOW.CRM.ALLOWED_STAGES') }}
      </p>
      <p v-if="!allowed.length" class="mb-1 text-xs text-n-slate-10">
        {{ t('AI_AGENTS.FLOW.CRM.ALL_STAGES') }}
      </p>
      <label
        v-for="s in stages"
        :key="s.name"
        class="flex items-center gap-2 py-1 text-sm text-n-slate-12"
      >
        <input
          type="checkbox"
          class="accent-[#00897b]"
          :checked="allowed.includes(s.name)"
          @change="toggleStage(s.name)"
        />
        {{ s.label }}
      </label>
    </template>

    <div v-for="action in ACTIONS" :key="action.key" class="mt-3">
      <label class="mb-1 block text-xs font-medium text-n-slate-11">
        {{ actionLabel(action.key) }}
      </label>
      <select
        :value="data[action.key] || action.fallback"
        class="w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12"
        @change="emit('update', { [action.key]: $event.target.value })"
      >
        <option v-for="m in MODES" :key="m" :value="m">
          {{ modeLabel(m) }}
        </option>
      </select>
    </div>
    <p class="mt-2 text-xs text-n-slate-10">
      {{ t('AI_AGENTS.FLOW.CRM.MODES_HINT') }}
    </p>

    <button
      class="mt-4 w-full rounded-lg border border-n-ruby-7 py-2 text-sm font-medium text-n-ruby-9 hover:bg-n-ruby-3"
      @click="emit('remove')"
    >
      {{ t('AI_AGENTS.FLOW.REMOVE_NODE') }}
    </button>
  </div>
</template>
